import {
  PortfolioAiCreationType,
  PortfolioAiVersion,
  PortfolioPhoto,
} from './types';

export const AI_CREATION_TYPE_LABELS: Record<PortfolioAiCreationType, string> = {
  image: 'Image',
  video: 'Video',
  audio: 'Audio',
  pdf: 'PDF',
  game_link: 'Game',
  claude_artifact: 'Claude',
  model_3d: '3D',
  link: 'Link',
};

export function slugifyName(value: string): string {
  return (
    String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'team'
  );
}

export function filenameSafe(value: string): string {
  return String(value || '')
    .replace(/[<>:"/\\|?*\u0000-\u001f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function photoExtension(photo: PortfolioPhoto): string {
  const fromPath = String(photo.storagePath || '').split('.').pop() || '';
  const fromUrl = String(photo.url || '').split('?')[0].split('.').pop() || '';
  const ext = (fromPath || fromUrl).toLowerCase().replace(/[^a-z0-9]/g, '');
  return ext || 'jpg';
}

function classDateForFilename(date?: string | null): string {
  const raw = String(date || '').trim();
  if (!raw) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  return filenameSafe(raw);
}

function joinFilenameParts(parts: Array<string | null | undefined>): string {
  return parts.map((part) => String(part || '').trim()).filter(Boolean).join('_');
}

export function portfolioDownloadStem(args: {
  teamName: string;
  squadName: string;
  classDate?: string | null;
  className?: string | null;
}): string {
  return joinFilenameParts([
    slugifyName(args.teamName),
    filenameSafe(args.squadName) || 'squad',
    classDateForFilename(args.classDate),
    filenameSafe(args.className || ''),
  ]);
}

export function portfolioPhotoFilename(args: {
  teamName: string;
  squadName: string;
  classDate?: string | null;
  className?: string | null;
  photoIndex: number;
  ext: string;
}): string {
  const ext = (args.ext || 'jpg').replace(/^\./, '');
  return `${portfolioDownloadStem(args)}_${args.photoIndex}.${ext}`;
}

function fileExtension(source: string): string {
  const cleaned = String(source || '').split('?')[0].split('#')[0].trim();
  const base = cleaned.split('/').pop() || cleaned;
  const match = base.match(/\.([a-z0-9]+)$/i);
  return match ? match[1].toLowerCase() : '';
}

function hostnameOf(source: string): string {
  try {
    if (!/^https?:\/\//i.test(source.trim())) return '';
    return new URL(source.trim()).hostname.replace(/^www\./i, '').toLowerCase();
  } catch {
    return '';
  }
}

export function detectAiCreationType(source: string): PortfolioAiCreationType {
  const raw = String(source || '').trim();
  if (!raw) return 'link';
  const host = hostnameOf(raw);
  if (host.includes('claude.ai') || host.includes('claude.site')) return 'claude_artifact';

  const ext = fileExtension(raw);
  if (['glb', 'gltf', 'stl'].includes(ext)) return 'model_3d';
  if (['png', 'jpg', 'jpeg', 'webp', 'gif'].includes(ext)) return 'image';
  if (['mp4', 'mov', 'webm'].includes(ext)) return 'video';
  if (['mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac', 'opus'].includes(ext)) return 'audio';
  if (ext === 'pdf') return 'pdf';
  if (host.includes('youtube.com') || host === 'youtu.be' || host.includes('vimeo.com')) {
    return 'video';
  }
  return 'link';
}

export function detectAiCreationFromFile(file: File): PortfolioAiCreationType {
  const fromName = detectAiCreationType(file.name);
  if (fromName !== 'link') return fromName;
  const mime = String(file.type || '').toLowerCase();
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('audio/')) return 'audio';
  if (mime.startsWith('video/')) return 'video';
  if (mime === 'application/pdf') return 'pdf';
  if (mime.includes('gltf') || mime.includes('stl') || mime === 'model/gltf-binary') return 'model_3d';
  return 'link';
}

export function youtubeVideoId(url: string): string | null {
  try {
    const parsed = new URL(url.trim());
    const host = parsed.hostname.replace(/^www\./i, '').toLowerCase();
    if (host === 'youtu.be') return parsed.pathname.replace(/^\//, '').split('/')[0] || null;
    if (host.includes('youtube.com')) {
      if (parsed.searchParams.get('v')) return parsed.searchParams.get('v');
      const parts = parsed.pathname.split('/').filter(Boolean);
      if (parts[0] === 'embed' || parts[0] === 'shorts' || parts[0] === 'live') {
        return parts[1] || null;
      }
    }
  } catch {
    return null;
  }
  return null;
}

export function vimeoVideoId(url: string): string | null {
  try {
    const parsed = new URL(url.trim());
    if (!parsed.hostname.replace(/^www\./i, '').toLowerCase().includes('vimeo.com')) return null;
    const id = parsed.pathname.split('/').filter(Boolean).pop() || '';
    return /^\d+$/.test(id) ? id : null;
  } catch {
    return null;
  }
}

export function youtubePosterUrl(url: string): string | null {
  const id = youtubeVideoId(url);
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;
}

export function videoEmbedUrl(url: string): string | null {
  const yt = youtubeVideoId(url);
  if (yt) return `https://www.youtube.com/embed/${yt}`;
  const vimeo = vimeoVideoId(url);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo}`;
  return null;
}

export function posterRequiredForType(_type: PortfolioAiCreationType): boolean {
  return false;
}

export function websitePreviewUrl(url: string): string | null {
  try {
    const parsed = new URL(String(url || '').trim());
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
    if (!parsed.hostname) return null;
    if (/firebasestorage\.googleapis\.com|storage\.googleapis\.com/i.test(parsed.hostname)) {
      return null;
    }
    return `https://s.wordpress.com/mshots/v1/${encodeURIComponent(parsed.toString())}?w=800`;
  } catch {
    return null;
  }
}

export function creationDisplayName(args: {
  file?: File | null;
  url?: string | null;
  filename?: string | null;
  caption?: string | null;
  type?: PortfolioAiCreationType | null;
}): string {
  const filename = String(args.filename || args.file?.name || '').trim();
  if (filename) return filename;
  const caption = String(args.caption || '').trim();
  const url = String(args.url || '').trim();
  try {
    if (url && /^https?:\/\//i.test(url)) {
      const parsed = new URL(url);
      const last = decodeURIComponent(parsed.pathname.split('/').filter(Boolean).pop() || '');
      if (last && /\.[a-z0-9]{2,5}$/i.test(last)) return last;
      const host = parsed.hostname.replace(/^www\./i, '');
      if (host) return host;
    }
  } catch {
    /* ignore */
  }
  if (caption) return caption;
  return AI_CREATION_TYPE_LABELS[args.type || 'link'] || 'Link';
}

export function sourceFilename(file?: File | null, url?: string | null): string {
  if (file?.name) return file.name;
  try {
    if (url && /^https?:\/\//i.test(url)) {
      const name = decodeURIComponent(new URL(url).pathname.split('/').pop() || '');
      if (name && name.includes('.')) return name;
    }
  } catch {
    /* ignore */
  }
  const raw = String(url || '').split('?')[0].split('#')[0];
  const fallback = raw.split('/').pop() || '';
  return fallback || 'PDF';
}

export function rangeSelectIds(
  photoIds: string[],
  anchorId: string | null,
  targetId: string
): string[] {
  const targetIndex = photoIds.indexOf(targetId);
  if (targetIndex < 0) return [targetId];
  const anchorIndex = anchorId ? photoIds.indexOf(anchorId) : -1;
  if (anchorIndex < 0) return [targetId];
  const from = Math.min(anchorIndex, targetIndex);
  const to = Math.max(anchorIndex, targetIndex);
  return photoIds.slice(from, to + 1);
}

export function photoNumbersForSources(
  photos: PortfolioPhoto[],
  sourcePhotoIds: string[] | undefined
): number[] {
  const ids = sourcePhotoIds || [];
  return ids
    .map((id) => photos.findIndex((photo) => photo.id === id) + 1)
    .filter((n) => n > 0);
}

export function provenanceLine(args: {
  photos: PortfolioPhoto[];
  gen: PortfolioAiVersion;
}): string {
  const numbers = photoNumbersForSources(args.photos, args.gen.sourcePhotoIds);
  const made =
    numbers.length > 0
      ? `Made from ${numbers.map((n) => `photo ${n}`).join(', ')}`
      : 'Made from selected photos';
  const who = args.gen.uploadedByName?.trim();
  return who ? `${made} · added by ${who}` : made;
}

export async function extractVideoPoster(file: File): Promise<Blob> {
  const url = URL.createObjectURL(file);
  const video = document.createElement('video');
  const waitFor = (event: 'loadeddata' | 'seeked', start: () => void) => new Promise<void>((resolve, reject) => {
    const cleanup = () => { clearTimeout(timer); video.removeEventListener(event, ready); video.removeEventListener('error', failed); };
    const ready = () => { cleanup(); resolve(); };
    const failed = () => { cleanup(); reject(new Error('Could not read video')); };
    const timer = window.setTimeout(failed, 8000);
    video.addEventListener(event, ready, { once: true });
    video.addEventListener('error', failed, { once: true });
    start();
  });
  try {
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    await waitFor('loadeddata', () => { video.src = url; });
    const seek = Math.min(0.15, (video.duration || 1) * 0.05);
    if (seek > 0 && Number.isFinite(seek)) await waitFor('seeked', () => { video.currentTime = seek; });
    const canvas = document.createElement('canvas');
    const width = video.videoWidth || 640, height = video.videoHeight || 360;
    const scale = Math.min(1, 1280 / width);
    canvas.width = Math.round(width * scale); canvas.height = Math.round(height * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not capture video frame');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.86));
    if (!blob) throw new Error('Could not capture video frame');
    return blob;
  } finally {
    video.removeAttribute('src'); video.load();
    URL.revokeObjectURL(url);
  }
}

export function triggerBlobDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noreferrer';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export async function fetchBytes(url: string): Promise<Uint8Array> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Could not download file (${response.status})`);
  return new Uint8Array(await response.arrayBuffer());
}
